import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Input, Textarea, Select, Label } from './ui/Form';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeslots = [
  'Morning (6AM - 10AM)',
  'Midday (10AM - 2PM)',
  'Afternoon (2PM - 6PM)',
  'Full Day (6AM - 6PM)',
];

const experienceTypes = ['Family', 'Barkada / Corporate', 'Solo', 'Learn-to-Fish'];

const addonOptions = [
  'Cabana (4 pax)',
  'Cabana (8 pax)',
  'BBQ Grill Set',
  'Bait Refill',
  'Catch-and-Keep',
  'Photographer add-on',
];

const today = new Date().toISOString().split('T')[0];

export default function BookingForm() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    visit_date: '',
    timeslot: timeslots[0],
    guests: 2,
    experience_type: experienceTypes[0],
    add_ons: [],
    notes: '',
  });

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const toggleAddon = (a) => {
    setForm((f) => ({
      ...f,
      add_ons: f.add_ons.includes(a)
        ? f.add_ons.filter((x) => x !== a)
        : [...f.add_ons, a],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        guests: Number(form.guests),
      });
      toast({
        title: 'Salamat! Your booking request is in.',
        description: 'Our team will confirm your slot via email or phone within 24 hours.',
      });
      setForm({
        full_name: '',
        email: '',
        phone: '',
        visit_date: '',
        timeslot: timeslots[0],
        guests: 2,
        experience_type: experienceTypes[0],
        add_ons: [],
        notes: '',
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast({
        title: 'Booking not sent',
        description: typeof detail === 'string' ? detail : 'Please check the fields and retry.',
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(() => {
    const parts = [
      form.experience_type,
      `${form.guests} ${form.guests === 1 ? 'pax' : 'pax'}`,
      form.timeslot,
    ];
    if (form.visit_date) parts.unshift(form.visit_date);
    return parts.join(' · ');
  }, [form]);

  return (
    <section id="book" data-testid="booking-section" className="py-20 md:py-32 bg-offwhite">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="overline text-muted mb-4">Book a visit</p>
            <h2 className="editorial-display text-pond text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
              Reserve your slot
              <br />
              <em className="italic font-light text-moss">in two minutes.</em>
            </h2>
            <p className="text-charcoal/80 text-base md:text-lg leading-relaxed mb-8">
              We'll confirm via email or text within 24 hours. Walk-ins are welcome, pero
              advance booking ensures cabana availability — lalo na sa weekends at holidays.
            </p>
            <div className="rounded-3xl bg-sand p-6 md:p-8 border border-moss/25">
              <p className="overline text-muted mb-2">Your selection</p>
              <p className="font-display text-2xl md:text-3xl text-pond leading-snug" data-testid="booking-summary">
                {summary || 'Pick your date and party size →'}
              </p>
              {form.add_ons.length > 0 && (
                <p className="mt-3 text-sm text-muted">
                  Add-ons: {form.add_ons.join(', ')}
                </p>
              )}
            </div>
          </div>

          <motion.form
            onSubmit={submit}
            data-testid="booking-form"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 rounded-3xl bg-sand border border-moss/25 p-7 md:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div>
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  data-testid="booking-name"
                  required
                  value={form.full_name}
                  onChange={set('full_name')}
                  placeholder="Juan Dela Cruz"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="booking-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="juan@email.com"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone / Viber</Label>
                <Input
                  id="phone"
                  data-testid="booking-phone"
                  required
                  value={form.phone}
                  onChange={set('phone')}
                  placeholder="+63 917 000 0000"
                />
              </div>
              <div>
                <Label htmlFor="guests">Number of Guests</Label>
                <Input
                  id="guests"
                  data-testid="booking-guests"
                  required
                  type="number"
                  min={1}
                  max={50}
                  value={form.guests}
                  onChange={set('guests')}
                />
              </div>
              <div>
                <Label htmlFor="visit_date">Visit Date</Label>
                <Input
                  id="visit_date"
                  data-testid="booking-date"
                  required
                  type="date"
                  min={today}
                  value={form.visit_date}
                  onChange={set('visit_date')}
                />
              </div>
              <div>
                <Label htmlFor="timeslot">Timeslot</Label>
                <Select
                  id="timeslot"
                  data-testid="booking-timeslot"
                  value={form.timeslot}
                  onChange={set('timeslot')}
                >
                  {timeslots.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="experience_type">Experience Type</Label>
                <Select
                  id="experience_type"
                  data-testid="booking-experience"
                  value={form.experience_type}
                  onChange={set('experience_type')}
                >
                  {experienceTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label>Add-ons (optional)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {addonOptions.map((a) => {
                    const active = form.add_ons.includes(a);
                    return (
                      <button
                        type="button"
                        key={a}
                        data-testid={`addon-${a.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                        onClick={() => toggleAddon(a)}
                        className={`text-sm rounded-full px-4 py-2 border transition-all duration-200 ${
                          active
                            ? 'bg-pond text-offwhite border-pond'
                            : 'bg-transparent text-charcoal border-moss/50 hover:border-pond'
                        }`}
                      >
                        {a}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="notes">Notes (allergies, requests, etc.)</Label>
                <Textarea
                  id="notes"
                  data-testid="booking-notes"
                  rows={3}
                  value={form.notes}
                  onChange={set('notes')}
                  placeholder="Sample: May 2 batang first-timers, dadalhin sana sa Learn-to-Fish."
                />
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <p className="text-xs text-muted max-w-md">
                By submitting, you agree to our visit policies and acknowledge that confirmation
                is subject to slot availability.
              </p>
              <Button
                type="submit"
                size="lg"
                disabled={loading}
                data-testid="booking-submit"
              >
                {loading ? 'Sending…' : 'Request Booking'}
              </Button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
